import React from "react";
import MenuComponent from "./MenuComponent/MenuComponent";
import "./OrderPageStyles.css"
import CartCardComponent from "../CartPage/CartCardComponent/CartCardComponent";
import { HiOutlineShoppingCart } from "react-icons/hi";
import CartComponent from "../CartPage/CartComponent/CartComponent";
import { useTheme } from "../../hooks/ThemeContext";
import { useAuth } from "../../hooks/useAuth";
import { getRemoteEnv } from '../../config/remoteEnv';
import { useOrderMode, ORDER_MODES } from "../../hooks/OrderModeContext";
import ToastService from "../../service/ToastService";
import OrderModeSelect from "../OrderMode/OrderModeSelect";
import Spinner from "../Spinner/Spinner";

type Props = {
    cart: any[],
    setCart: React.Dispatch<React.SetStateAction<any[]>>,
    productData: any[],
    menuSettings: any[],
    additionalCharges: any[],
    testMode?: boolean,
    openingHours: any,
    singleStoreConfig: any,
}


const OrderPage = (props: Props) => {

    const auth = useAuth();

    React.useEffect(() => {
        if (auth?.user) {
            console.log("On Orders Page")
            console.log("USER: " + auth?.user?.email)
            console.log("ROLES: " + auth?.roles)
        }
    }, [auth])

    const containerRef = React.useRef<HTMLDivElement | null>(null);

    const { theme } = useTheme();
    const orderMode = useOrderMode();

    function arraysEqual<T>(arr1: T[], arr2: T[]): boolean {
        // Check if arrays have the same length
        if (arr1.length !== arr2.length) {
          return false;
        }
      
        // Helper function to deep compare two values
        const deepEqual = (a: any, b: any): boolean => {
          if (a === b) return true;
          
          if (a == null || b == null) return a === b;
          
          if (typeof a !== typeof b) return false;
          
          if (typeof a === 'object') {
            if (Array.isArray(a) !== Array.isArray(b)) return false;
            
            if (Array.isArray(a)) {
              if (a.length !== b.length) return false;
              return a.every((item, index) => deepEqual(item, b[index]));
            }
            
            const keysA = Object.keys(a);
            const keysB = Object.keys(b);
            
            if (keysA.length !== keysB.length) return false;
            
            return keysA.every(key => keysB.includes(key) && deepEqual(a[key], b[key]));
          }
          
          return false;
        };
      
        // Create a copy of arr2 to track which items we've matched
        const arr2Copy = [...arr2];
        
        // For each item in arr1, find and remove a matching item from arr2Copy
        for (const item1 of arr1) {
          const matchIndex = arr2Copy.findIndex(item2 => deepEqual(item1, item2));
          
          if (matchIndex === -1) {
            return false; // No matching item found
          }
          
          // Remove the matched item to prevent duplicate matching
          arr2Copy.splice(matchIndex, 1);
        }
        
        // If we've matched all items, arr2Copy should be empty
        return arr2Copy.length === 0;
    }

    // Returns false when the add was BLOCKED (closed mode) so callers never
    // announce success for an item that went nowhere.
    const addToCart = (newEntry: any): boolean => {

        // Store config still loading: whether this mode is open is unknown
        // for another beat. Blocking with an honest message beats the old
        // behaviour (empty openingHours read as "Store is currently
        // closed!") and beats sneaking an item into a mode that resolves
        // closed a second later.
        if (!orderMode.ready) {
            ToastService.showToast("Just a moment — still loading store info. Try again in a second.", { variant: "info" });
            return false;
        }

        // Item count tracking: products with a stockCount can't be added
        // beyond what's left, counting what the cart already holds (add-ons
        // are products too, consumed once per parent unit).
        {
            const need = new Map<string, number>();
            const consume = (entry: any) => {
                const q = Number(entry?.quantity) || 1;
                if (entry?.id) need.set(entry.id, (need.get(entry.id) || 0) + q);
                for (const addonId of entry?.additionalOptionIds || []) {
                    if (addonId) need.set(addonId, (need.get(addonId) || 0) + q);
                }
            };
            props.cart.forEach(consume);
            consume(newEntry);
            // Judge ONLY the products this new entry touches — a pre-existing
            // overdraft elsewhere in the cart (stock dropped after adding)
            // must not block adding unrelated items; pay-press cleans those.
            const involved = new Set<string>([newEntry?.id, ...(newEntry?.additionalOptionIds || [])].filter(Boolean));
            for (const [pid, qty] of Array.from(need.entries())) {
                if (!involved.has(pid)) continue;
                const p = props.productData.find((x: any) => x.productId === pid);
                if (!p || typeof p.stockCount !== "number") continue;
                if (qty > p.stockCount) {
                    ToastService.showToast(
                        p.stockCount <= 0
                            ? `${p.productName} has just sold out`
                            : `Only ${p.stockCount} of ${p.productName} left`,
                        { variant: "warning" }
                    );
                    return false;
                }
            }
        }

        // Closed modes are browsable but not orderable — every add funnels
        // through here, so this one gate covers cards, modals and add-ons.
        if (orderMode.mode && !orderMode.modeOpen) {
            // Paused by the venue (switch or dated closure): every mode is
            // closed — say so in their words, with when it's back.
            if (orderMode.pause) {
                ToastService.showToast(orderMode.pause.message, { variant: "warning" });
                return false;
            }
            const label = ORDER_MODES.find((m) => m.id === orderMode.mode)?.label || "This mode";
            const status = orderMode.modeInfo[orderMode.mode];
            const reopen = status?.label?.startsWith("Closed · ") ? ` — ${status.label.slice(9)}` : "";
            ToastService.showToast(`${label} ordering is closed right now${reopen}`, { variant: "warning" });
            return false;
        }

        let existingItem: any;

        for (let product of props.cart) {
            // Reward lines (paid in points) never merge with money lines.
            if (product.id === newEntry.id && !product.loyaltyReward && arraysEqual(product.additionalOptionIds, newEntry.additionalOptionIds) && arraysEqual(product.selectedChoices, newEntry.selectedChoices) && product.additionalRequests === newEntry.additionalRequests) {
                existingItem = product;
                existingItem.quantity += newEntry.quantity;
            }
        }

        if (existingItem) {
            props.setCart(prevCart => prevCart.map((p) => (p.id === existingItem.id && arraysEqual(p.additionalOptionIds, existingItem.additionalOptionIds) && arraysEqual(p.selectedChoices, existingItem.selectedChoices) && (p.additionalRequests === existingItem.additionalRequests) ? existingItem : p)))
        }
        else {
            props.setCart(prevCart => [...prevCart, newEntry])
        }

        return true;
    }

    // Fulfilment first: until a ?mode= is chosen (and the store takes
    // orders), the menu gives way to the big Delivery / Pick Up / Dine In
    // chooser. Menu-only sites skip this entirely.
    //
    // Before the store config loads we can't know whether a chooser is
    // needed — rendering the menu meanwhile flashed it for a beat before
    // the chooser appeared. Hold a spinner for that moment instead (a mode
    // already in the URL skips the wait entirely).
    const awaitingMode =
        !getRemoteEnv().menuOnly &&
        !orderMode.mode &&
        (!orderMode.ready || orderMode.orderingEnabled);

    // The cart panel's presence drives the desktop rail layout (top bar
    // narrowed + its cart button hidden, menu padded) via body.dp-cart-rail
    // — the same condition that renders the panel below. A body class rather
    // than CSS :has() so browsers without :has() (Firefox < 121) don't get
    // the rail overlaying a full-width top bar.
    const showCartPanel =
        !awaitingMode &&
        !getRemoteEnv().fullCatalogueView &&
        ((!getRemoteEnv().menuOnly && props.singleStoreConfig?.general.onlineOrderingEnabled) ||
            window.location.hostname.includes("demo."));
    React.useEffect(() => {
        document.body.classList.toggle("dp-cart-rail", !!showCartPanel);
        return () => { document.body.classList.remove("dp-cart-rail"); };
    }, [showCartPanel]);

    if (awaitingMode) {
        return (
            <div style={{ width: "100%", height: "100%", overflowY: "auto", backgroundColor: theme.backgroundColor }}>
                {orderMode.ready ? (
                    <OrderModeSelect />
                ) : (
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", width: "100%", minHeight: "60vh" }}>
                        <Spinner color={theme.loaderColor} />
                    </div>
                )}
            </div>
        );
    }

    // overflow-x CLIP (not hidden): hidden turns the element into a
    // scrollport, which captures the menu header's position:sticky in
    // doc-flow mode; clip trims without doing that. BUT clip (unlike
    // hidden) keeps min-width:auto content-based on flex items — without
    // an explicit minWidth:0 the menu column lays out at its content's
    // intrinsic width (~680px) and never shrinks below it, overflowing
    // every viewport narrower than that.
    return (
        <div style={{display: "flex", width: "100%", minWidth: 0, height: "100%", overflowX: "clip"}}>
            <div className="menu-web-container" ref={containerRef} style={{height: "100%", minWidth: 0, color: theme.textColor, backgroundColor: theme.backgroundColor, flexGrow: 1, overflowX: "clip"}}>
                <MenuComponent containerRef={containerRef} productData={props.productData} menuSettings={props.menuSettings} addToCart={addToCart} openingHours={props.openingHours}/>
            </div>
            
            {showCartPanel && <div className="cart-web-container" style={{width: "30%", minWidth: "300px", boxSizing: "border-box", backgroundColor: theme.cartBackgroundColor, userSelect: "none"}}>
                <CartComponent cart={props.cart} setCart={props.setCart} productData={props.productData} menuSettings={props.menuSettings} openingHours={props.openingHours} additionalCharges={props.additionalCharges} testMode={props.testMode || false} singleStoreConfig={props.singleStoreConfig}/>
            </div>}
        </div>
    )

}

export default OrderPage;